import type { ReactNode } from "react";
import { getStrings, homeShellString } from "../i18n/strings";

type Strings = ReturnType<typeof getStrings>;

type UseCaseIconType = "vps" | "proxy" | "fraud" | "monitoring";

// Four short, concrete scenarios — not marketing copy, and deliberately no headline statistics:
// nothing in the repo or the database backs a "1M+ checks / 200+ sources / 99.9% uptime" row, so the
// reference design's stats band is not reproduced here (spec §二十八). Pure/stateless: the same four
// titles/texts are also rendered at build time into the per-locale SEO snapshots (see
// scripts/homeSeoSnapshot.ts), which carry the text without these decorative icons.
export function HomeUseCases({ strings }: { strings: Strings }) {
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);

  const cases: Array<{ icon: UseCaseIconType; title: string; text: string }> = [
    { icon: "vps", title: t("homeUseCaseVpsTitle"), text: t("homeUseCaseVpsText") },
    { icon: "proxy", title: t("homeUseCaseProxyTitle"), text: t("homeUseCaseProxyText") },
    { icon: "fraud", title: t("homeUseCaseFraudTitle"), text: t("homeUseCaseFraudText") },
    { icon: "monitoring", title: t("homeUseCaseMonitoringTitle"), text: t("homeUseCaseMonitoringText") },
  ];

  return (
    <section className="home-usecases" aria-labelledby="home-usecases-title">
      <h2 id="home-usecases-title" className="home-section-title">{t("homeUseCasesTitle")}</h2>
      <div className="home-usecases__grid">
        {cases.map((item) => (
          <div className={`home-usecases__item home-usecases__item--${item.icon}`} key={item.title}>
            <span className="home-usecases__icon" aria-hidden="true"><UseCaseIcon type={item.icon} /></span>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function UseCaseIcon({ type }: { type: UseCaseIconType }) {
  const paths: Record<UseCaseIconType, ReactNode> = {
    vps: <><rect x="3" y="4" width="18" height="7" rx="1.5" /><rect x="3" y="13" width="18" height="7" rx="1.5" /><path d="M7 7.5h.01M7 16.5h.01" /></>,
    proxy: <><rect x="3" y="6" width="18" height="12" rx="2.5" /><path d="M8 12h.01M12 12h.01M16 12h.01" /></>,
    fraud: <><path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" /><path d="M12 8.5v4M12 15.5h.01" /></>,
    monitoring: <><path d="M3 12h4l2.5-6 4 12L16 12h5" /></>,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[type]}
    </svg>
  );
}
